import { navigate } from 'gatsby';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { ProfessionalInfoResponse } from '@/api/services/ProfessionalProfile/types';
import { paths } from '@/constants/paths';
import { useGetProfessional } from '@/hooks/professional/useGetProfessional';

import { useAuthContext } from './AuthContext';

interface ProfileContextType {
  professionalData: ProfessionalInfoResponse;
  setProfessionalData: (data: ProfessionalInfoResponse) => void;
  getInfoProfessional: () => Promise<void>;
  updateProfessionalData: (data: Partial<ProfessionalInfoResponse>) => void;
}

const ProfileContext = createContext({} as ProfileContextType);

interface ProfileContextProviderProps {
  children: React.ReactNode;
}

export function ProfileContextProvider({ children }: ProfileContextProviderProps) {
  const { isLoggedIn, handleLogout } = useAuthContext();
  const { execute } = useGetProfessional();

  const [professionalData, setProfessionalData] = useState<ProfessionalInfoResponse>(
    {} as ProfessionalInfoResponse,
  );
  const getInfoProfessional = useCallback(async () => {
    try {
      const professionalInfo = await execute();
      setProfessionalData(professionalInfo);
    } catch {
      navigate(paths.home);
    }
  }, []);

  const updateProfessionalData = (data: Partial<ProfessionalInfoResponse>) => {
    setProfessionalData((prevProfessionalData) => ({
      ...prevProfessionalData,
      ...data,
    }));
  };

  useEffect(() => {
    const infoProfessional = async () => {
      if (isLoggedIn) {
        try {
          await getInfoProfessional();
        } catch {
          handleLogout();
        }
      }
    };

    infoProfessional();
  }, [isLoggedIn, handleLogout, getInfoProfessional]);

  return (
    <ProfileContext.Provider
      value={{
        professionalData,
        setProfessionalData,
        getInfoProfessional,
        updateProfessionalData,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export const useProfileContext = () => useContext(ProfileContext);
